import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, Tag, Table, Rate, Select, Space, Empty, Alert, Avatar, Button, Divider, Descriptions, Modal, Input, DatePicker, Form, Upload, message } from 'antd';
import { WalletOutlined, CalendarOutlined, StarOutlined, UserOutlined, LoginOutlined, LogoutOutlined, ClockCircleOutlined, DownloadOutlined, FileTextOutlined, IdcardOutlined, FilePdfOutlined, SendOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') : '';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEARS = [];
{
  const y = new Date().getFullYear();
  for (let i = y - 3; i <= y + 1; i++) YEARS.push(i);
}

export default function MySalary() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clock, setClock] = useState(new Date());
  const [todayAtt, setTodayAtt] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveModal, setLeaveModal] = useState(false);
  const [leaveForm] = Form.useForm();
  const [profileModal, setProfileModal] = useState(false);
  const [profileForm] = Form.useForm();
  const [myRequests, setMyRequests] = useState([]);
  const [profileSaving, setProfileSaving] = useState(false);
  const clockRef = useRef(null);

  useEffect(() => {
    clockRef.current = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(clockRef.current);
  }, []);

  const loadMyRequests = useCallback(async () => {
    try {
      const res = await api.get('/staff/my-profile');
      setMyRequests(Array.isArray(res.data?.requests) ? res.data.requests : []);
    } catch (e) { setMyRequests([]); }
  }, []);

  useEffect(() => { loadMyRequests(); }, [loadMyRequests]);

  const openProfileModal = () => {
    const p = data?.profile || {};
    profileForm.setFieldsValue({
      full_name: data?.user?.full_name || '',
      designation: p.designation || '',
      phone: p.phone || '',
      phone2: p.phone2 || '',
      personal_email: p.personal_email || '',
      joining_date: p.joining_date ? dayjs(p.joining_date) : null,
      aadhar_number: p.aadhar_number || '',
      pan_number: p.pan_number || '',
      address: p.address || '',
      notes: p.notes || '',
      staff_note: '',
    });
    setProfileModal(true);
  };

  // Staff apni details bhejta hai; admin approve karega tab hi profile me lagti hai.
  const submitProfileRequest = async (v) => {
    setProfileSaving(true);
    try {
      const fd = new FormData();
      ['full_name', 'designation', 'phone', 'phone2', 'personal_email', 'aadhar_number', 'pan_number', 'address', 'notes', 'staff_note']
        .forEach(f => { if (v[f]) fd.append(f, v[f]); });
      if (v.joining_date) fd.append('joining_date', v.joining_date.format('YYYY-MM-DD'));
      ['photo', 'aadhar_doc', 'offer_letter', 'joining_letter'].forEach(f => {
        const up = v[f];
        if (up && up.file) fd.append(f, up.file.originFileObj || up.file);
      });
      await api.post('/staff/my-profile-request', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      message.success('Request admin ke paas bhej di gayi. Approve hone par details update ho jayengi.');
      setProfileModal(false);
      loadMyRequests();
    } catch (e) {
      message.error(e.response?.data?.error || 'Request bhejne me dikkat aayi');
    } finally { setProfileSaving(false); }
  };

  useEffect(() => {
    api.get('/staff/my-today').then(r => setTodayAtt(r.data)).catch(() => {});
    api.get('/staff/leave-requests').then(r => setLeaveRequests(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const submitLeave = async (vals) => {
    try {
      await api.post('/staff/leave-request', {
        leave_type: vals.leave_type,
        from_date: vals.dates[0].format('YYYY-MM-DD'),
        to_date: vals.dates[1].format('YYYY-MM-DD'),
        reason: vals.reason,
      });
      message.success('Leave request submitted!');
      setLeaveModal(false);
      leaveForm.resetFields();
      api.get('/staff/leave-requests').then(r => setLeaveRequests(Array.isArray(r.data) ? r.data : []));
    } catch (e) { message.error('Failed to submit leave request'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/staff/my-hr?month=${month}&year=${year}`);
      setData(res.data);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const downloadPDF = (targetMonth, targetYear, targetData) => {
    const d = targetData || data;
    if (!d) return;
    const m = targetMonth || month;
    const y = targetYear || year;
    const p = d.profile || {};
    const a = d.attendance_summary || {};
    const attRows = (d.attendance || []).map(r =>
      `<tr><td>${dayjs(r.att_date).format('DD MMM YYYY (ddd)')}</td><td style="color:${r.status === 'present' ? '#52c41a' : r.status === 'absent' ? '#ff4d4f' : r.status === 'holiday' ? '#722ed1' : '#faad14'};font-weight:bold">${(r.status || '').replace('_', ' ').toUpperCase()}</td><td>${r.check_in_time || '—'}</td><td>${r.check_out_time || '—'}</td></tr>`
    ).join('');
    const html = `<!DOCTYPE html><html><head><title>Monthly Report - ${d.user.full_name} - ${MONTHS[m-1]} ${y}</title>
    <style>body{font-family:Arial,sans-serif;padding:30px;max-width:900px;margin:auto}
    .header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px;text-align:center}
    .header h1{margin:0;font-size:22px}.header p{margin:5px 0 0;opacity:0.9}
    h2{color:#333;border-bottom:2px solid #667eea;padding-bottom:8px;margin-top:25px}
    table{width:100%;border-collapse:collapse;margin:10px 0}
    td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}
    th{background:#f0f5ff;font-weight:bold}
    .summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:15px 0}
    .summary-box{background:#f9f9f9;border-radius:8px;padding:12px;text-align:center;border:1px solid #eee}
    .summary-box .label{font-size:11px;color:#888;text-transform:uppercase}.summary-box .value{font-size:22px;font-weight:bold;margin-top:4px}
    .highlight{color:#52c41a}.deduction{color:#ff4d4f}.purple{color:#722ed1}
    .salary-card{background:#f0f5ff;border:2px solid #667eea;border-radius:12px;padding:20px;margin:15px 0}
    .net-salary{font-size:28px;font-weight:bold;color:#52c41a;text-align:center;margin:10px 0}
    .footer{text-align:center;color:#999;font-size:11px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}
    @media print{body{padding:15px}.header{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>
    <div class="header"><h1>${d.user.full_name} — Monthly Report</h1><p>${MONTHS[m-1]} ${y} | Bainsla Music Private Limited</p></div>
    <h2>Profile</h2>
    <table><tr><th>Name</th><td>${d.user.full_name}</td><th>Designation</th><td>${p.designation||'—'}</td></tr>
    <tr><th>Phone</th><td>${p.phone||'—'}</td><th>Email</th><td>${p.personal_email||d.user?.email||'—'}</td></tr>
    <tr><th>Joining Date</th><td>${p.joining_date||'—'}</td><th>Status</th><td>${p.status||'active'}</td></tr></table>
    <h2>Attendance Summary</h2>
    <div class="summary-grid">
      <div class="summary-box"><div class="label">Working Days</div><div class="value">${a.working_days||0}</div></div>
      <div class="summary-box"><div class="label">Present</div><div class="value highlight">${a.present||0}</div></div>
      <div class="summary-box"><div class="label">Absent</div><div class="value deduction">${a.absent||0}</div></div>
      <div class="summary-box"><div class="label">Half Day</div><div class="value" style="color:#faad14">${a.half_day||0}</div></div>
      <div class="summary-box"><div class="label">Leave</div><div class="value">${a.leave||0}</div></div>
      <div class="summary-box"><div class="label">Holiday</div><div class="value purple">${a.holiday||0}</div></div>
      <div class="summary-box"><div class="label">Days in Month</div><div class="value">${a.days_in_month||0}</div></div>
      <div class="summary-box"><div class="label">Sundays (off)</div><div class="value">${a.sundays||0}</div></div>
    </div>
    <h2>Salary Breakdown</h2>
    <div class="salary-card">
      <table style="border:none"><tr><th style="background:transparent;border:none">Monthly Salary</th><td style="border:none;font-size:16px">₹${Number(a.monthly_salary||0).toLocaleString('en-IN')}</td>
      <th style="background:transparent;border:none">Per Day</th><td style="border:none;font-size:16px">₹${Number(a.per_day_salary||0).toLocaleString('en-IN')}</td></tr>
      <tr><th style="background:transparent;border:none">Absent (${Number(a.absent||0)} day)</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.absent_deduction ?? a.total_deduction ?? 0).toLocaleString('en-IN')}</td>
      ${Number(a.pre_join_days||0) > 0 ? `<th style="background:transparent;border:none">Before Joining (${a.pre_join_days} day)</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.pre_join_deduction||0).toLocaleString('en-IN')}</td>` : '<th style="background:transparent;border:none"></th><td style="border:none"></td>'}</tr>
      <tr><th style="background:transparent;border:none">Total Deduction</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.total_deduction||0).toLocaleString('en-IN')}</td>
      <th style="background:transparent;border:none">Net Salary</th><td style="border:none"></td></tr></table>
      <div class="net-salary">₹${Number(a.net_salary||0).toLocaleString('en-IN')}</div>
    </div>
    <h2>Daily Attendance</h2>
    <table><tr><th>Date</th><th>Status</th><th>Check-In</th><th>Check-Out</th></tr>${attRows || '<tr><td colspan="4" style="text-align:center;color:#999">No attendance records</td></tr>'}</table>
    ${d.current_salary ? `<h2>Payment Status</h2><table><tr><th>Amount</th><td>₹${Number(d.current_salary.amount||0).toLocaleString('en-IN')}</td><th>Status</th><td>${d.current_salary.status}</td></tr><tr><th>Mode</th><td>${d.current_salary.mode||'—'}</td><th>Paid Date</th><td>${d.current_salary.paid_date||'—'}</td></tr></table>` : ''}
    ${latestPerf ? `<h2>Performance</h2><table><tr><th>Rating</th><td>${'★'.repeat(latestPerf.rating)}${'☆'.repeat(5 - latestPerf.rating)}</td><th>Remark</th><td>${latestPerf.remark||'—'}</td></tr></table>` : ''}
    <div class="footer">Generated on ${new Date().toLocaleString('en-IN')} — Bainsla Music Data Dashboard | data.intubemedia.com</div>
    </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  };

  const downloadOtherMonthPDF = async (m, y) => {
    try {
      message.loading({ content: `Loading ${MONTHS[m-1]} ${y} report...`, key: 'pdf' });
      const res = await api.get(`/staff/my-hr?month=${m}&year=${y}`);
      downloadPDF(m, y, res.data);
      message.success({ content: 'PDF ready!', key: 'pdf' });
    } catch (err) {
      message.error({ content: 'Failed to load report', key: 'pdf' });
    }
  };

  const statusTag = (s) => s === 'paid' ? <Tag color="success">Paid</Tag> : <Tag color="warning">Pending</Tag>;
  const attTag = (s) => ({
    present: <Tag color="success">Present</Tag>,
    absent: <Tag color="error">Absent</Tag>,
    half_day: <Tag color="warning">Half-day</Tag>,
    not_marked: <Tag color="default">Not Marked</Tag>,
    leave: <Tag color="default">Leave</Tag>,
    holiday: <Tag color="purple">Holiday</Tag>,
  }[s] || <Tag>{s}</Tag>);

  const cur = data?.current_salary;
  const att = data?.attendance_summary || { present: 0, half_day: 0, leave: 0, absent: 0, holiday: 0, present_days: 0 };
  const latestPerf = (data?.performance_history || [])[0];

  return (
    <div>
      {/* Live Clock + Today's Attendance */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 12, background: 'linear-gradient(135deg, #667eea22, #764ba222)', textAlign: 'center' }}>
            <ClockCircleOutlined style={{ fontSize: 24, color: '#667eea' }} />
            <div style={{ fontSize: 32, fontWeight: 700, color: '#333', marginTop: 4 }}>
              {clock.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
            </div>
            <div style={{ color: '#888', fontSize: 13 }}>
              {clock.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={16}>
          <Card style={{ borderRadius: 12, background: todayAtt?.checked_in ? 'linear-gradient(135deg, #52c41a11, #52c41a22)' : 'linear-gradient(135deg, #ff4d4f11, #ff4d4f22)' }}>
            <Row gutter={16} align="middle">
              <Col xs={12}>
                <Statistic title="Today Check-in" value={todayAtt?.check_in_time ? todayAtt.check_in_time : 'Not yet'}
                  prefix={<LoginOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: todayAtt?.check_in_time ? '#52c41a' : '#999', fontSize: 20 }} />
              </Col>
              <Col xs={12}>
                <Statistic title="Today Check-out" value={todayAtt?.check_out_time ? todayAtt.check_out_time : 'Pending'}
                  prefix={<LogoutOutlined style={{ color: todayAtt?.check_out_time ? '#ff4d4f' : '#999' }} />}
                  valueStyle={{ color: todayAtt?.check_out_time ? '#ff4d4f' : '#999', fontSize: 20 }} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }} wrap>
        <Title level={3} style={{ margin: 0 }}><WalletOutlined /> My Salary & Performance</Title>
        <Space>
          <Select value={month} onChange={setMonth} style={{ width: 110 }} options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} />
          <Select value={year} onChange={setYear} style={{ width: 100 }} options={YEARS.map(y => ({ value: y, label: y }))} />
          <Button icon={<FilePdfOutlined />} onClick={downloadPDF} disabled={!data}>Download PDF</Button>
        </Space>
      </Space>

      <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Salary is paid on the 10th of every month." />

      {data && (
        <Card style={{ borderRadius: 12, marginBottom: 16 }} loading={loading} title={<span><UserOutlined /> My Profile</span>}
          extra={<Button icon={<EditOutlined />} onClick={openProfileModal}>Apni details bharo / sudharo</Button>}>
          {myRequests.some(r => r.status === 'pending') && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message="Aapki details change request admin ke paas pending hai. Approve hone par yahan update ho jayegi." />
          )}
          {!data.profile && (
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message="Aapki details abhi khali hai — 'Apni details bharo' se bhar do, admin approve karega." />
          )}
          <Row gutter={16} align="middle">
            {data.profile?.photo && (
              <Col xs={24} sm={4} style={{ textAlign: 'center', marginBottom: 12 }}>
                <Avatar size={72} src={API_BASE + data.profile.photo} icon={<UserOutlined />} />
              </Col>
            )}
            <Col xs={24} sm={data.profile?.photo ? 20 : 24}>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
                <Descriptions.Item label="Name"><Text strong>{data.user.full_name}</Text></Descriptions.Item>
                <Descriptions.Item label="Designation">{data.profile?.designation || '—'}</Descriptions.Item>
                <Descriptions.Item label="Monthly Salary">₹{Number(data.profile?.monthly_salary || 0).toLocaleString('en-IN')} <Tag color="blue">admin only</Tag></Descriptions.Item>
                <Descriptions.Item label="Joining Date">{data.profile?.joining_date || '—'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{data.profile?.phone || '—'}</Descriptions.Item>
                <Descriptions.Item label="Phone 2 (Family)">{data.profile?.phone2 || '—'}</Descriptions.Item>
                <Descriptions.Item label="Personal Email">{data.profile?.personal_email || '—'}</Descriptions.Item>
                <Descriptions.Item label="Aadhar Number">{data.profile?.aadhar_number || '—'}</Descriptions.Item>
                <Descriptions.Item label="PAN Number">{data.profile?.pan_number || '—'}</Descriptions.Item>
                <Descriptions.Item label="Address" span={3}>{data.profile?.address || '—'}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
          {myRequests.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Divider orientation="left" style={{ margin: '8px 0' }}>Meri details change requests</Divider>
              <Table size="small" rowKey="id" pagination={false} dataSource={myRequests}
                columns={[
                  { title: 'Bheji', dataIndex: 'created_at', render: v => dayjs(v).format('DD MMM YYYY, hh:mm A') },
                  { title: 'Fields', dataIndex: 'changes', render: v => Object.keys(v || {}).map(f => <Tag key={f}>{f.replace(/_/g, ' ')}</Tag>) },
                  { title: 'Status', dataIndex: 'status', render: v => <Tag color={v === 'approved' ? 'green' : v === 'rejected' ? 'red' : 'orange'}>{v.toUpperCase()}</Tag> },
                  { title: 'Admin remark', dataIndex: 'admin_remark', render: v => v || <Text type="secondary">—</Text> },
                ]} />
            </div>
          )}
          {(data.profile?.offer_letter || data.profile?.joining_letter || data.profile?.aadhar_doc) && (
            <div style={{ marginTop: 12 }}>
              <Divider orientation="left" style={{ margin: '8px 0' }}>Documents</Divider>
              <Space wrap>
                {data.profile.offer_letter && (
                  <a href={API_BASE + data.profile.offer_letter} target="_blank" rel="noreferrer" download>
                    <Button icon={<FileTextOutlined />} type="primary" ghost size="small">Download Offer Letter</Button>
                  </a>
                )}
                {data.profile.joining_letter && (
                  <a href={API_BASE + data.profile.joining_letter} target="_blank" rel="noreferrer" download>
                    <Button icon={<FileTextOutlined />} type="primary" ghost size="small">Download Joining Letter</Button>
                  </a>
                )}
                {data.profile.aadhar_doc && (
                  <a href={API_BASE + data.profile.aadhar_doc} target="_blank" rel="noreferrer">
                    <Button icon={<IdcardOutlined />} size="small">View Aadhar Card</Button>
                  </a>
                )}
              </Space>
            </div>
          )}
        </Card>
      )}

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: 12, marginBottom: 16 }} loading={loading}
            title={<span><CalendarOutlined /> Attendance</span>}>
            <Statistic title="Present Days" value={att.present_days} />
            <div style={{ marginTop: 8 }}>
              <Tag color="success">Present: {att.present}</Tag>
              <Tag color="warning">Half: {att.half_day}</Tag>
              <Tag color="default">Leave: {att.leave}</Tag>
              <Tag color="error">Absent: {att.absent}</Tag>
              <Tag color="purple">Holiday: {att.holiday || 0}</Tag>
            </div>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Working Days: {att.working_days || '—'} (Sundays off: {att.sundays || 0})</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: 12, marginBottom: 16 }} loading={loading}
            title={<span><WalletOutlined /> {MONTHS[month - 1]} {year} Salary</span>}>
            {cur ? (
              <>
                <Statistic value={Number(cur.amount)} prefix="₹" />
                <div style={{ marginTop: 8 }}>{statusTag(cur.status)} <Tag>{cur.mode}</Tag></div>
                {cur.paid_date && <Text type="secondary">Paid on {cur.paid_date}</Text>}
              </>
            ) : <Alert type="warning" showIcon message="This month's salary has not been recorded yet (pending)" />}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: 12, marginBottom: 16 }} loading={loading}
            title={<span><StarOutlined /> Performance</span>}>
            {latestPerf ? (
              <>
                <Rate disabled value={latestPerf.rating} />
                <div style={{ marginTop: 8 }}><Text type="secondary">{latestPerf.remark || 'No remark'}</Text></div>
              </>
            ) : <Text type="secondary">No reviews yet</Text>}
          </Card>
        </Col>
      </Row>

      <Card style={{ borderRadius: 12, marginBottom: 16, background: '#f0f5ff', borderColor: '#adc6ff' }} loading={loading}
        title="💰 Salary Breakdown (Sunday off)">
        <Row gutter={[16, 12]}>
          <Col xs={12} sm={6}><Statistic title="Days in Month" value={att.days_in_month || '—'} /></Col>
          <Col xs={12} sm={6}><Statistic title="Sundays (off)" value={att.sundays || 0} /></Col>
          <Col xs={12} sm={6}><Statistic title="Working Days" value={att.working_days || '—'} valueStyle={{ fontWeight: 'bold' }} /></Col>
          <Col xs={12} sm={6}><Statistic title="Per Day Salary" value={att.per_day_salary || 0} prefix="₹" precision={0} /></Col>
          <Col xs={12} sm={8}><Statistic title="Monthly Salary" value={att.monthly_salary || 0} prefix="₹" precision={0} /></Col>
          <Col xs={12} sm={8}><Statistic title={`Total Deduction${att.pre_join_days > 0 ? ` (incl. ${att.pre_join_days}d pre-join)` : ''}`} value={att.total_deduction || 0} prefix="- ₹" precision={0} valueStyle={{ color: '#ff4d4f' }} /></Col>
          <Col xs={12} sm={8}><Statistic title="Net Salary" value={att.net_salary || 0} prefix="₹" precision={0} valueStyle={{ color: '#52c41a', fontWeight: 'bold', fontSize: 22 }} /></Col>
        </Row>
      </Card>

      <Card style={{ borderRadius: 12, marginBottom: 16, background: '#fff7e6', borderColor: '#ffd591' }} loading={loading}
        title={<span><WalletOutlined /> Advance Salary — {MONTHS[month - 1]} {year}</span>}>
        <Row gutter={[16, 12]}>
          <Col xs={12} sm={8}><Statistic title="Net Salary (this month)" value={att.net_salary || 0} prefix="₹" precision={0} /></Col>
          <Col xs={12} sm={8}><Statistic title={`Advance (${MONTHS[month - 1]})${Number(att.advance_carry_in || 0) > 0 ? ` — incl. ₹${Math.round(Number(att.advance_carry_in)).toLocaleString('en-IN')} from previous month` : ''}`} value={Number(att.advance_amount || 0) + Number(att.advance_carry_in || 0)} prefix="- ₹" precision={0} valueStyle={{ color: '#d4380d', fontWeight: 'bold' }} /></Col>
          <Col xs={12} sm={8}><Statistic title="Net Payable" value={att.net_payable ?? att.net_salary ?? 0} prefix="₹" precision={0} valueStyle={{ color: '#52c41a', fontWeight: 'bold', fontSize: 22 }} /></Col>
          {Number(att.advance_carry_out || 0) > 0 && (
            <Col xs={12} sm={8}><Statistic title="Extra advance → carried to next month" value={att.advance_carry_out} prefix="₹" precision={0} valueStyle={{ color: '#cf1322', fontWeight: 'bold' }} /></Col>
          )}
        </Row>
        {(data?.advances || []).length > 0 ? (
          <Table size="small" style={{ marginTop: 12 }} rowKey="id" pagination={false}
            columns={[
              { title: 'Payment Date', dataIndex: 'given_date', render: v => dayjs(v).format('DD MMM YYYY') },
              { title: 'Salary Month', key: 'sm', render: (_, r) => <Tag color="blue">{r.salary_month ? dayjs(`${r.salary_year}-${String(r.salary_month).padStart(2, '0')}-01`).format('MMM YYYY') : dayjs(r.given_date).format('MMM YYYY')}</Tag> },
              { title: 'Type', dataIndex: 'type', render: v => <Tag color={v === 'loan' ? 'volcano' : 'orange'}>{v}</Tag> },
              { title: 'Amount', dataIndex: 'amount', render: v => `₹${Number(v).toLocaleString('en-IN')}` },
              { title: 'Reason', dataIndex: 'reason', render: v => v || <Text type="secondary">—</Text> },
            ]}
            dataSource={data.advances} />
        ) : (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>Is mahine koi advance nahi li.</Text>
        )}
      </Card>

      <Card style={{ borderRadius: 12, marginBottom: 16 }} title={<span><ClockCircleOutlined /> Attendance (this month) — Check-in/Out</span>} loading={loading}>
        <Table size="small" rowKey={(r) => r.att_date} pagination={false}
          columns={[
            { title: 'Date', dataIndex: 'att_date', render: v => dayjs(v).format('DD MMM YYYY (ddd)') },
            { title: 'Status', dataIndex: 'status', render: attTag },
            { title: 'Check-In', dataIndex: 'check_in_time', render: v => v ? <Tag color="green"><LoginOutlined /> {v}</Tag> : <Text type="secondary">—</Text> },
            { title: 'Check-Out', dataIndex: 'check_out_time', render: v => v ? <Tag color="red"><LogoutOutlined /> {v}</Tag> : <Text type="secondary">—</Text> },
          ]}
          dataSource={data?.attendance || []}
          locale={{ emptyText: <Empty description="No attendance this month" /> }} />
      </Card>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card style={{ borderRadius: 12 }} title="Salary History" loading={loading}>
            <Table size="small" rowKey={(r) => `${r.year}-${r.month}`} pagination={false}
              columns={[
                { title: 'Period', key: 'p', render: (_, r) => `${MONTHS[r.month - 1]} ${r.year}` },
                { title: 'Amount', dataIndex: 'amount', render: (v) => `₹${Number(v).toLocaleString('en-IN')}` },
                { title: 'Status', dataIndex: 'status', render: statusTag },
                { title: 'Proof', key: 'proof', render: (_, r) => r.proof_path ? <a href={`${API_BASE}/${r.proof_path}`} target="_blank" rel="noreferrer">View</a> : <Text type="secondary">—</Text> },
              ]}
              dataSource={data?.salary_history || []}
              locale={{ emptyText: <Empty description="No history" /> }} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card style={{ borderRadius: 12 }} title="Performance History" loading={loading}>
            <Table size="small" rowKey={(r) => `${r.year}-${r.month}`} pagination={false}
              columns={[
                { title: 'Period', key: 'p', render: (_, r) => `${MONTHS[r.month - 1]} ${r.year}` },
                { title: 'Rating', dataIndex: 'rating', render: (v) => <Rate disabled value={v} style={{ fontSize: 13 }} /> },
                { title: 'Remark', dataIndex: 'remark', render: (v) => v || '—' },
              ]}
              dataSource={data?.performance_history || []}
              locale={{ emptyText: <Empty description="No history" /> }} />
          </Card>
        </Col>
      </Row>

      {/* My Monthly Reports — Quick Download */}
      <Card style={{ borderRadius: 12, marginTop: 16, background: '#f0f5ff', borderColor: '#adc6ff' }}
        title={<span><FilePdfOutlined style={{ color: '#667eea' }} /> My Monthly Reports</span>}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Click on any month to download your complete monthly report (attendance + salary + performance)</Text>
        <Row gutter={[12, 12]}>
          {(() => {
            const cards = [];
            const currentMonth = new Date().getMonth() + 1;
            const currentYear = new Date().getFullYear();
            for (let i = 0; i < 6; i++) {
              let m = currentMonth - i;
              let y = currentYear;
              if (m <= 0) { m += 12; y -= 1; }
              const isCurrentlySelected = m === month && y === year;
              cards.push(
                <Col xs={12} sm={8} md={4} key={`${y}-${m}`}>
                  <Card size="small" hoverable
                    style={{ borderRadius: 10, textAlign: 'center', borderColor: isCurrentlySelected ? '#667eea' : '#e8e8e8', background: isCurrentlySelected ? '#e6f0ff' : '#fff' }}
                    onClick={() => {
                      if (isCurrentlySelected && data) { downloadPDF(); }
                      else { downloadOtherMonthPDF(m, y); }
                    }}>
                    <div style={{ fontWeight: 'bold', fontSize: 16, color: '#333' }}>{MONTHS[m - 1]}</div>
                    <div style={{ color: '#888', fontSize: 12 }}>{y}</div>
                    <FilePdfOutlined style={{ fontSize: 20, color: '#667eea', marginTop: 8 }} />
                    <div style={{ fontSize: 11, color: '#667eea', marginTop: 4 }}>Download PDF</div>
                  </Card>
                </Col>
              );
            }
            return cards;
          })()}
        </Row>
      </Card>

      {/* Leave Requests */}
      <Card style={{ borderRadius: 12, marginTop: 16 }}
        title={<span><CalendarOutlined /> My Leave Requests</span>}
        extra={<Button type="primary" size="small" icon={<SendOutlined />} onClick={() => setLeaveModal(true)}>Apply Leave</Button>}>
        <Table size="small" rowKey="id" pagination={false}
          columns={[
            { title: 'Type', dataIndex: 'leave_type', render: v => <Tag color={v === 'sick' ? 'red' : v === 'earned' ? 'blue' : 'orange'}>{(v || '').toUpperCase()}</Tag> },
            { title: 'From', dataIndex: 'from_date', render: v => dayjs(v).format('DD MMM YYYY') },
            { title: 'To', dataIndex: 'to_date', render: v => dayjs(v).format('DD MMM YYYY') },
            { title: 'Days', dataIndex: 'days' },
            { title: 'Reason', dataIndex: 'reason', render: v => v || '—' },
            { title: 'Status', dataIndex: 'status', render: v => <Tag color={v === 'approved' ? 'green' : v === 'rejected' ? 'red' : 'orange'}>{(v || '').toUpperCase()}</Tag> },
            { title: 'Admin Remark', dataIndex: 'admin_remark', render: v => v || '—' },
          ]}
          dataSource={leaveRequests}
          locale={{ emptyText: <Empty description="No leave requests" /> }} />
      </Card>

      <Modal title="Meri details — bharo / sudharo (admin approve karega)" open={profileModal}
        onCancel={() => setProfileModal(false)} footer={null} width={720} destroyOnClose>
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Salary ka option yahan nahi hai — salary sirf admin badal sakta hai. Baaki details bharo, admin approve karte hi lag jayengi." />
        <Form form={profileForm} layout="vertical" onFinish={submitProfileRequest}>
          <Row gutter={12}>
            <Col xs={24} sm={12}><Form.Item name="full_name" label="Naam"><Input placeholder="Poora naam" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="designation" label="Designation"><Input placeholder="e.g. Distribution Executive" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="phone" label="Phone (Primary)"><Input placeholder="Mobile number" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="phone2" label="Phone 2 (Parents/Family emergency)"><Input placeholder="Ghar ka number" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="personal_email" label="Personal Email"><Input placeholder="Personal email" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="joining_date" label="Joining Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="aadhar_number" label="Aadhar Card Number"><Input maxLength={12} placeholder="12 digit Aadhar" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="pan_number" label="PAN Number"><Input maxLength={10} placeholder="10 character PAN" /></Form.Item></Col>
          </Row>
          <Form.Item name="address" label="Address"><Input.TextArea rows={2} placeholder="Permanent address" /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Divider orientation="left">Photo & Documents</Divider>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item name="photo" label="Meri Photo" valuePropName="file">
                <Upload beforeUpload={() => false} maxCount={1} accept="image/*"><Button icon={<UploadOutlined />}>Photo chuno</Button></Upload>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="aadhar_doc" label="Aadhar Card (image/PDF)" valuePropName="file">
                <Upload beforeUpload={() => false} maxCount={1} accept="image/*,.pdf"><Button icon={<UploadOutlined />}>File chuno</Button></Upload>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="offer_letter" label="Offer Letter (PDF/image)" valuePropName="file">
                <Upload beforeUpload={() => false} maxCount={1} accept="image/*,.pdf"><Button icon={<UploadOutlined />}>File chuno</Button></Upload>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="joining_letter" label="Joining Letter (PDF/image)" valuePropName="file">
                <Upload beforeUpload={() => false} maxCount={1} accept="image/*,.pdf"><Button icon={<UploadOutlined />}>File chuno</Button></Upload>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="staff_note" label="Admin ke liye note (optional)">
            <Input.TextArea rows={2} placeholder="Kya galat tha / kya theek karna hai" />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={profileSaving} block>
            Admin ko approval ke liye bhejo
          </Button>
        </Form>
      </Modal>

      <Modal title="Apply for Leave" open={leaveModal} onCancel={() => setLeaveModal(false)} footer={null}>
        <Form form={leaveForm} layout="vertical" onFinish={submitLeave}>
          <Form.Item name="leave_type" label="Leave Type" initialValue="casual" rules={[{ required: true }]}>
            <Select options={[
              { value: 'casual', label: 'Casual Leave' },
              { value: 'sick', label: 'Sick Leave' },
              { value: 'earned', label: 'Earned Leave' },
            ]} />
          </Form.Item>
          <Form.Item name="dates" label="From - To Date" rules={[{ required: true, message: 'Select dates' }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={3} placeholder="Reason for leave..." />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Submit Leave Request</Button>
        </Form>
      </Modal>
    </div>
  );
}
